/**
 * The complete `data-analytics-*` vocabulary read by the delegated listener.
 *
 * Authored via {@link analyticsSurface} (containers) and {@link trackableProps}
 * (individual elements) — never by hand, so values stay compile-time checked.
 */
export const ANALYTICS_ATTRS = {
  // --- Layer 2: element declares its own semantics -------------------------
  componentType: 'data-analytics-component-type',
  componentName: 'data-analytics-component-name',
  actionType: 'data-analytics-action-type',
  componentIndex: 'data-analytics-component-index',
  componentsTotal: 'data-analytics-components-total',

  /** Typed `Location` union, read by Layer 2's curated event. */
  location: 'data-analytics-location',

  // --- Layer 1 --------------------------------------------------------------
  /**
   * Presence registers a container for capture during the pilot. Carries no
   * value — it is a gate, not context, and it has no effect on payload contents.
   */
  surface: 'data-analytics-surface',
  /**
   * Search session id, inherited via `closest()`.
   *
   * The only inherited context left. Everything else that lived here —
   * entity kind, entity id, availability — was an interpretation of the DOM and
   * now travels as raw ancestor testids for Data Analytics to interpret.
   */
  searchId: 'data-analytics-search-id',

  // --- Escape hatches -------------------------------------------------------
  /** Declares a subtree's testids unsafe to emit (interpolated free text). */
  untrustedTestids: 'data-analytics-untrusted-testids',
  /** Marks a subtree as owned by a manual (Layer 3) call — suppresses capture. */
  manual: 'data-analytics-manual',
  /** Marks a subtree as never capturable (PII / sensitive). */
  exclude: 'data-analytics-exclude',
} as const;

export type AnalyticsAttrName =
  (typeof ANALYTICS_ATTRS)[keyof typeof ANALYTICS_ATTRS];

export type AnalyticsDataAttributes = Partial<
  Record<AnalyticsAttrName, string>
>;

/**
 * Elements treated as the unit of interaction by Layer 1 capture.
 *
 * Used for *subtree* queries (`querySelectorAll`). For the upward walk on every
 * click, prefer the tag/role sets below — matching a seven-part union selector
 * against every ancestor is markedly slower than two set lookups.
 */
export const INTERACTIVE_SELECTOR =
  'button, a, [role="button"], [role="tab"], [role="link"], [role="switch"], [role="menuitem"]';

export const INTERACTIVE_TAGS = new Set(['BUTTON', 'A']);
export const INTERACTIVE_ROLES = new Set([
  'button',
  'tab',
  'link',
  'switch',
  'menuitem',
]);

/**
 * Builds a runtime allowlist that is compile-time checked in both directions:
 * every array entry must belong to the union, and every union member must
 * appear in the array (adding a union value without updating the array is a
 * type error, and vice versa).
 */
export function exhaustiveAllowlist<Union extends string>() {
  return <const T extends readonly Union[]>(
    values: Union extends T[number] ? T : never
  ): T => values;
}
